import { FormEvent, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { QrCode, ArrowLeft, Copy, Check, Loader2, ShieldCheck } from 'lucide-react'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import AuthLayout from '@/components/AuthLayout'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface TwoFactorSetupAppProps {
  qrCodeSvg: string
  otpSecret: string
  enableUrl: string
  backUrl: string
  locale: string
  logoUrl: string
  translations: {
    setupAppTitle: string
    scanQr: string
    manualEntry: string
    enterCodeToEnable: string
    enableButton: string
    back: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function TwoFactorSetupApp({
  qrCodeSvg,
  otpSecret,
  enableUrl,
  backUrl,
  locale,
  logoUrl,
  translations: t,
}: TwoFactorSetupAppProps) {
  const [otp, setOtp] = useState('')
  const [processing, setProcessing] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post(
      enableUrl,
      { otp_attempt: otp },
      { onFinish: () => setProcessing(false) },
    )
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(otpSecret)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <>
      <Head title={t.setupAppTitle} />

      <AuthLayout locale={locale} logoUrl={logoUrl}>
        <div className="rounded-2xl bg-white shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10">
          <div className="text-center mb-6">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2DD4BF]/10 to-[#8B5CF6]/10">
              <QrCode className="h-7 w-7 text-[#8B5CF6]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#252854] mb-1">{t.setupAppTitle}</h1>
            <p className="text-sm text-slate-400">{t.scanQr}</p>
          </div>

          {/* QR Code */}
          <div className="flex justify-center mb-5">
            <div
              className="rounded-xl bg-white p-4 shadow-md border border-slate-100"
              dangerouslySetInnerHTML={{ __html: qrCodeSvg }}
            />
          </div>

          {/* Manual secret */}
          <div className="text-center mb-6">
            <p className="text-xs text-slate-400 mb-2">{t.manualEntry}</p>
            <div className="inline-flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
              <code className="text-xs font-mono text-[#252854] break-all select-all">
                {otpSecret}
              </code>
              <button
                type="button"
                onClick={handleCopy}
                className="shrink-0 text-slate-400 hover:text-[#8B5CF6] transition-colors"
                title="Copy"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-[#2DD4BF]" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <p className="text-sm text-center text-slate-400 mb-5">
            {t.enterCodeToEnable}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={setOtp}
                autoFocus
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button
              type="submit"
              disabled={processing || otp.length < 6}
              className="w-full h-11 rounded-xl text-sm font-medium bg-gradient-to-r from-[#2DD4BF] to-[#8B5CF6] hover:from-[#14B8A6] hover:to-[#7C3AED] text-white shadow-lg shadow-[#8B5CF6]/20 transition-all duration-200"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  {t.enableButton}
                </span>
              )}
            </Button>
          </form>

          <div className="mt-5 text-center">
            <a
              href={backUrl}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#8B5CF6] hover:text-[#7C3AED] transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t.back}
            </a>
          </div>
        </div>
      </AuthLayout>
    </>
  )
}
